import { getCurrentLanguage, getCurrentUnitSystem } from 'harmony-language';
import { getTimezone } from '../../utils/date-time-utils';
import { CargoFilterMode, OnTimeReportMode } from './on-time/on-time-report';

export const getUserSettings = () => {
    const language = getCurrentLanguage();
    const units = getCurrentUnitSystem();
    const timezone = getTimezone();

    return {
        language,
        units,
        timezone,
    }
}

export const getDriverEfficiencyUrlAndParams = (
    useReplica: boolean,
    organizationId: number,
    dateBegin: string,
    dateEnd: string,
    driverIds: number[],
    originLocationIds: number[],
    employmentTypeIds: number[]
) => {
    const reportUrl = new URL(`${API_BASE}/api/reports/driver-efficiency`);
    const reportParams = new URLSearchParams(reportUrl.search);
    reportParams.set('useReplica', String(useReplica));
    reportParams.set('organizationId', String(organizationId));
    reportParams.set('dateBegin', dateBegin);
    reportParams.set('dateEnd', dateEnd);
    driverIds.forEach(driverId => {
        reportParams.append('driverIds[]', driverId.toString());
    });
    originLocationIds.forEach(originLocationId => {
        reportParams.append('originLocationIds[]', originLocationId.toString());
    });
    if (employmentTypeIds.length === 1) {
        if (employmentTypeIds[0] === 1) {
            reportParams.set('isContract', 'true');
        } else {
            reportParams.set('isContract', 'false');
        }
    }

    return {
        reportUrl,
        reportParams,
    }
}

export const getDecisionUrlAndParams = (
    useReplica: boolean,
    organizationId: number,
    dateBegin: string,
    dateEnd: string,
    siteModes: string[],
    siteTypeIds: number[],
    siteIds: number[],
) => {
    const reportUrl = new URL(`${API_BASE}/api/reports/decision`);
    const reportParams = new URLSearchParams(reportUrl.search);
    reportParams.set('useReplica', String(useReplica));
    reportParams.set('organizationId', String(organizationId));
    reportParams.set('dateBegin', dateBegin);
    reportParams.set('dateEnd', dateEnd);
    siteModes.forEach(siteMode => {
        reportParams.append('siteModes[]', siteMode);
    });
    siteTypeIds.forEach(siteTypeId => {
        reportParams.append('siteTypeIds[]', siteTypeId.toString());
    });
    siteIds.forEach(siteId => {
        reportParams.append('siteIds[]', siteId.toString());
    });

    return {
        reportUrl,
        reportParams,
    }
}

export const getSequencingUrlAndParams = (
    useReplica: boolean,
    organizationId: number,
    dateBegin: string,
    dateEnd: string,
    trailerIds: number[],
    originSiteIds: number[],
    destinationSiteIds: number[],
) => {
    const reportUrl = new URL(`${API_BASE}/api/reports/sequencing-feed`);
    const reportParams = new URLSearchParams(reportUrl.search);
    reportParams.set('useReplica', String(useReplica));
    reportParams.set('organizationId', String(organizationId));
    reportParams.set('dateBegin', dateBegin);
    reportParams.set('dateEnd', dateEnd);
    trailerIds.forEach(trailerId => {
        reportParams.append('trailerIds[]', trailerId.toString());
    });
    originSiteIds.forEach(originSiteId => {
        reportParams.append('originSiteIds[]', originSiteId.toString());
    });
    destinationSiteIds.forEach(destinationSiteId => {
        reportParams.append('destinationSiteIds[]', destinationSiteId.toString());
    });

    return {
        reportUrl,
        reportParams,
    }
}

export const getSplitStopUrlAndParams = (
    useReplica: boolean,
    organizationId: number,
    dateBegin: string,
    dateEnd: string,
    locationIds: number[],
) => {
    const reportUrl = new URL(`${API_BASE}/api/reports/split-stop`);
    const reportParams = new URLSearchParams(reportUrl.search);

    reportParams.set('useReplica', String(useReplica));
    reportParams.set('organizationId', String(organizationId));
    reportParams.set('dateBegin', dateBegin);
    reportParams.set('dateEnd', dateEnd);
    locationIds.forEach(locationId => {
        reportParams.append('locationIds[]', locationId.toString());
    });

    return {
        reportUrl,
        reportParams,
    }
};

export const getOnTimeUrlAndParams = (
    useReplica: boolean,
    organizationId: number,
    dateBegin: string,
    dateEnd: string,
    mode: OnTimeReportMode,
    stopTypes: string[],
    customerIds: number[],
    cargoFilterMode: CargoFilterMode,
    cargoTypeIds: number[],
    cargoCategories: string[]
) => {
    const reportUrl = new URL(`${API_BASE}/api/reports/on-time`);
    const reportParams = new URLSearchParams(reportUrl.search);

    reportParams.set('useReplica', String(useReplica));
    reportParams.set('organizationId', String(organizationId));
    reportParams.set('dateBegin', dateBegin);
    reportParams.set('dateEnd', dateEnd);
    reportParams.set('mode', mode);
    reportParams.set('cargoFilterMode', cargoFilterMode);
    stopTypes.forEach(stopType => {
        reportParams.append('stopTypes[]', stopType);
    });
    customerIds.forEach(customerId => {
        reportParams.append('customerIds[]', customerId.toString());
    });
    cargoTypeIds.forEach(cargoTypeId => {
        reportParams.append('cargoTypeIds[]', cargoTypeId.toString());
    });
    cargoCategories.forEach(cargoCategory => {
        reportParams.append('cargoCategories[]', cargoCategory);
    });

    return {
        reportUrl,
        reportParams,
    }
};

export function getLateStopsUrlAndParams(
    useReplica: boolean,
    organizationId: number,
    dateBegin: string,
    dateEnd: string,
    stopTypes: string[],
    customerIds: number[],
    cargoTypeIds: number[],
) {
    const reportUrl = new URL(`${API_BASE}/api/reports/late-stops`);
    const reportParams = new URLSearchParams(reportUrl.search);

    reportParams.set('csv', 'true'); // Late stops report is always CSV
    reportParams.set('useReplica', String(useReplica));
    reportParams.set('organizationId', String(organizationId));
    reportParams.set('dateBegin', dateBegin);
    reportParams.set('dateEnd', dateEnd);
    stopTypes.forEach(stopType => {
        reportParams.append('stopTypes[]', stopType);
    });
    customerIds.forEach(customerId => {
        reportParams.append('customerIds[]', customerId.toString());
    });
    cargoTypeIds.forEach(cargoTypeId => {
        reportParams.append('cargoTypeIds[]', cargoTypeId.toString());
    });

    return {
        reportUrl,
        reportParams,
    }
}

export function getSiteIssuesUrlAndParams(
    useReplica: boolean,
    organizationId: number,
    dateBegin: string,
    dateEnd: string,
    siteIds: number[],
    siteIssueTypeIds: number[],
    isResolved: number[]
) {
    const reportUrl = new URL(`${API_BASE}/api/reports/site-issues`);
    const reportParams = new URLSearchParams(reportUrl.search);

    reportParams.set('useReplica', String(useReplica));
    reportParams.set('organizationId', String(organizationId));
    reportParams.set('dateBegin', dateBegin);
    reportParams.set('dateEnd', dateEnd);
    siteIds.forEach(siteId => {
        reportParams.append('siteIds[]', siteId.toString());
    });
    siteIssueTypeIds.forEach(issueTypeId => {
        reportParams.append('siteIssueTypeIds[]', issueTypeId.toString());
    });
    if (isResolved.length === 1) {
        if (isResolved[0] === 1) {
            reportParams.set('isResolved', 'true');
        } else {
            reportParams.set('isResolved', 'false');
        }
    }

    return {
        siteIssuesUrl: reportUrl,
        siteIssuesParams: reportParams
    }
}